// src/ui/StoryViewer.jsx — watching stories in the browser (the phone's twin is
// src/components/StoryViewer.tsx). Click right → next, left → back, hold the
// mouse (or Space) → pause, ← → keys, Esc closes. The look is drawn by the same
// <Overlay> a post uses (filter, text, stickers); a song plays under a video.
// Someone else's: reply or react. Mine: who watched, their reactions and
// replies, and delete.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Overlay } from './Overlay';
import { storyDuration, firstUnseen, storyAge, STORY_REACTIONS } from '../services/stories';
import { markStoryViewed, reactStory, replyStory, deleteStory, fetchStoryViewers } from '../services/storyApi';
import { X, Trash, Send, Eye2 } from './icons';

export default function StoryViewer({ groups, startGroup, seen, me, onClose, onSeen, onProfile, onDeleted }) {
  const [gi, setGi] = useState(startGroup);
  const [si, setSi] = useState(() => firstUnseen(groups[startGroup], seen));
  const [paused, setPaused] = useState(false);
  const [ready, setReady] = useState(false);
  const [pct, setPct] = useState(0);
  const [reply, setReply] = useState('');
  const [typing, setTyping] = useState(false);
  const [note, setNote] = useState('');
  const [sheet, setSheet] = useState(null);
  const raf = useRef(0);
  const clock = useRef({ start: 0, spent: 0 });
  const video = useRef(null);
  const audio = useRef(null);
  const total = useRef(5000);

  const group = groups[gi];
  const story = group ? group.stories[si] : null;
  const mine = !!story && story.userId === me.uid;
  const holding = paused || typing || !!sheet;

  const next = useCallback(() => {
    if (!group) { onClose(); return; }
    if (si + 1 < group.stories.length) { setSi(si + 1); return; }
    if (gi + 1 < groups.length) { const ng = gi + 1; setGi(ng); setSi(firstUnseen(groups[ng], seen)); return; }
    onClose();
  }, [group, si, gi, groups, seen, onClose]);
  const prev = useCallback(() => {
    if (si > 0) { setSi(si - 1); return; }
    if (gi > 0) { const pg = gi - 1; setGi(pg); setSi(Math.max(0, groups[pg].stories.length - 1)); return; }
    clock.current = { start: performance.now(), spent: 0 }; setPct(0);
    if (video.current) video.current.currentTime = (story?.trimStartMs || 0) / 1000;
  }, [si, gi, groups, story]);

  /* a new story: reset, count the view, its song */
  useEffect(() => {
    if (!story) return undefined;
    setReady(false); setPct(0);
    clock.current = { start: 0, spent: 0 };
    total.current = storyDuration(story);
    if (!mine) markStoryViewed(story.id).catch(() => {});
    onSeen(story.id);
    if (audio.current) { audio.current.pause(); audio.current = null; }
    if (story.type === 'video' && story.audioTrackUrl && story.isAddedSoundMuted === false) {
      const a = new Audio(story.audioTrackUrl);
      a.loop = true;
      a.volume = Math.max(0, Math.min(1, (Number(story.addedSoundVolume) || 100) / 100));
      a.addEventListener('loadedmetadata', () => { try { a.currentTime = (Number(story.audioStartMs) || 0) / 1000; } catch {} }, { once: true });
      audio.current = a;
    }
    return () => { if (audio.current) { audio.current.pause(); audio.current = null; } };
  }, [gi, si]); // eslint-disable-line react-hooks/exhaustive-deps

  /* the bar */
  useEffect(() => {
    if (!story || !ready) return undefined;
    if (holding) {
      if (clock.current.start) { clock.current.spent += performance.now() - clock.current.start; clock.current.start = 0; }
      video.current?.pause(); audio.current?.pause();
      return undefined;
    }
    video.current?.play().catch(() => {});
    audio.current?.play().catch(() => {});
    clock.current.start = performance.now();
    const tick = () => {
      const spent = clock.current.spent + (performance.now() - clock.current.start);
      const p = Math.min(1, spent / total.current);
      setPct(p);
      if (p >= 1) { next(); return; }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [ready, holding, gi, si]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const key = (e) => {
      if (typing) return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === ' ') { e.preventDefault(); setPaused((v) => !v); }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [next, prev, onClose, typing]);

  const flash = (m) => { setNote(m); setTimeout(() => setNote(''), 1600); };
  const send = async () => {
    const t = reply.trim(); if (!t || !story) return;
    setReply('');
    const r = await replyStory(story.id, t);
    flash(r && r.ok ? 'Sent' : 'That didn’t go through');
  };
  const react = (e) => { if (!story) return; flash(e); reactStory(story.id, e).catch(() => {}); };
  const viewers = async () => {
    setSheet({ loading: true });
    const r = await fetchStoryViewers(story.id);
    setSheet(r && r.ok ? r : { failed: true, viewers: [], replies: [] });
  };
  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this story? It will be removed for everyone, with its views and replies.')) return;
    const r = await deleteStory(story.id);
    if (r && r.ok) { onDeleted(story.id); onClose(); } else flash('That didn’t go through');
  };

  if (!group || !story) return null;
  const trimStart = (Number(story.trimStartMs) || 0) / 1000;
  const trimEnd = Number(story.trimEndMs) > 0 ? Number(story.trimEndMs) / 1000 : null;

  return (
    <div className="vb-sv" role="dialog" aria-label="Story">
      <div className="vb-sv-stage">
        <div className="vb-sv-media">
          {story.type === 'video' ? (
            <video
              key={story.id} ref={video} src={story.url} playsInline
              muted={story.isOriginalMuted === true}
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                if (trimStart) v.currentTime = trimStart;
                const d = Math.max(0, (trimEnd || v.duration) - trimStart) * 1000;
                if (d > 0) total.current = Math.min(30000, d);
                v.volume = Math.max(0, Math.min(1, (Number(story.originalVolume) || 100) / 100));
                setReady(true);
              }}
              onTimeUpdate={(e) => { if (trimEnd && e.currentTarget.currentTime >= trimEnd) e.currentTarget.pause(); }}
              onError={() => setReady(true)}
            />
          ) : (
            <img key={story.id} src={story.url} alt="" onLoad={() => setReady(true)} onError={() => setReady(true)} />
          )}
          <Overlay post={story} me={me} className="vb-sv-ov" />
          <div className="vb-sv-tap left" onClick={prev} onMouseDown={() => setPaused(true)} onMouseUp={() => setPaused(false)} onMouseLeave={() => paused && setPaused(false)} />
          <div className="vb-sv-tap right" onClick={next} onMouseDown={() => setPaused(true)} onMouseUp={() => setPaused(false)} onMouseLeave={() => paused && setPaused(false)} />
        </div>
        <div className="vb-sv-top">
          <div className="vb-sv-bars">
            {group.stories.map((s, i) => (
              <span key={s.id}><i style={{ width: `${i < si ? 100 : i > si ? 0 : Math.round(pct * 100)}%` }} /></span>
            ))}
          </div>
          <div className="vb-sv-who">
            <button className="vb-sv-person" onClick={() => onProfile(group)}>
              {group.image ? <img src={group.image} alt="" /> : <span />}
              <b>{mine ? 'Your story' : group.name}</b>
              <em>{storyAge(story.createdAt, Date.now())} · {story.audience === 'friends' ? 'Friends' : 'Everyone'}</em>
            </button>
            {mine && <button className="vb-sv-icon" onClick={remove} title="Delete story"><Trash width={18} height={18} /></button>}
            <button className="vb-sv-icon" onClick={onClose} title="Close (Esc)"><X width={20} height={20} /></button>
          </div>
          {story.audioTrackTitle && story.isAddedSoundMuted === false && <div className="vb-sv-song">♪ {story.audioTrackTitle}{story.audioTrackArtist ? ` · ${story.audioTrackArtist}` : ''}</div>}
        </div>
        <div className="vb-sv-bottom">
          {note && <div className="vb-sv-note">{note}</div>}
          {mine ? (
            <button className="vb-sv-viewers" onClick={viewers}><Eye2 width={16} height={16} /> {Number(story.viewCount) || 0} viewers</button>
          ) : (
            <>
              <div className="vb-sv-reacts">{STORY_REACTIONS.map((e) => <button key={e} onClick={() => react(e)}>{e}</button>)}</div>
              <div className="vb-sv-reply">
                <input value={reply} onChange={(e) => setReply(e.target.value)} onFocus={() => setTyping(true)} onBlur={() => setTyping(false)}
                  onKeyDown={(e) => { if (e.key === 'Enter') send(); }} placeholder={`Reply to ${group.name}…`} maxLength={500} />
                {reply.trim() && <button onClick={send} aria-label="Send"><Send width={18} height={18} /></button>}
              </div>
            </>
          )}
        </div>
        {sheet && (
          <div className="vb-sv-sheet" onClick={() => setSheet(null)}>
            <div onClick={(e) => e.stopPropagation()}>
              <div className="vb-sv-sheet-head"><b>{(sheet.viewers || []).length} viewers</b><button onClick={() => setSheet(null)}><X width={16} height={16} /></button></div>
              {sheet.loading ? <div className="vb-sub">Loading…</div> : (
                <>
                  {(sheet.replies || []).map((r) => (
                    <div className="vb-sv-row" key={`r${r.id}`}>{r.image ? <img src={r.image} alt="" /> : <span />}<div><b>{r.name}</b><i>“{r.text}”</i></div></div>
                  ))}
                  {(sheet.viewers || []).map((v) => (
                    <div className="vb-sv-row" key={`v${v.uid}`}>{v.image ? <img src={v.image} alt="" /> : <span />}<div><b>{v.name}</b></div>{v.r && <em>{v.r}</em>}</div>
                  ))}
                  {!(sheet.viewers || []).length && !(sheet.replies || []).length && <div className="vb-sub">{sheet.failed ? 'That didn’t load' : 'No one has seen this story yet'}</div>}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
